<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ExchangeRateSourceSpecEditor',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'update:modelValue']);

  const props = defineProps({
    ...userComponentProps,
    modelValue: {
      type: Object,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const emitChange = (...args) => {
    let value = item.value;
    if (_.isEmpty(value.source) || _.isEmpty(value.rate_source)) {
      value = null;
    }
    emit('update:modelValue', value);
  };

  const item = ref(null);
  const sources = ref(null);
  const rateSources = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    item: item.value,
    sources: sources.value,
    rateSources: rateSources.value,
    ux: ux.value,
  }));

  const $$load_item = () => {
    return (
      props.modelValue || {
        source: null,
        rate_source: null,
        invoice_description: null,
      }
    );
  };
  const $$load_sources = () => {
    return $app.value.repo.get('list_exchange_rate_source');
  };
  const $$load_rateSources = () => {
    return $app.value.repo.get('ui_metadata', {
      path: 'exchange_rate_rate_source_type_options',
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'sources' },
      { name: 'rateSources' },
      { name: 'ux' },
    ],
    loaders: {
      item: $$load_item,
      sources: $$load_sources,
      rateSources: $$load_rateSources,
    },
    ux: {},
    dataRefs: { item, sources, rateSources, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.modelValue,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modelValue');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );

  const act_8320016629450276_script_chamn6u1j_value_callable = async (
    $event
  ) => {
    return emitChange();
  };
  const act_8320016629450276_script_chamn6u1j_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_8320016629450276_script_chamn6u1j_value_callable($event);
      },
    };
  };
  const act_1040167445267876_script_s7bhsosw4_value_callable = async (
    $event
  ) => {
    return emitChange();
  };
  const act_1040167445267876_script_s7bhsosw4_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1040167445267876_script_s7bhsosw4_value_callable($event);
      },
    };
  };
  const act_1548630417156826_script_kx7eso0gn_value_callable = async (
    $event
  ) => {
    return emitChange();
  };
  const act_1548630417156826_script_kx7eso0gn_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1548630417156826_script_kx7eso0gn_value_callable($event);
      },
    };
  };

  const act_8320016629450276_script_chamn6u1j = new actionTypes.ScriptAction({
    actionArgs: act_8320016629450276_script_chamn6u1j_getActionArgs,
    displayName: 'emitChange',
  });
  const inst_evh_8320016629450276_input_vb07wv1i5 =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_script_chamn6u1j,
      event: 'input',
      displayName: 'emitChange',
    });
  const inst_evh_8320016629450276_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8320016629450276_input_vb07wv1i5],
    event: 'input',
  });
  const act_1040167445267876_script_s7bhsosw4 = new actionTypes.ScriptAction({
    actionArgs: act_1040167445267876_script_s7bhsosw4_getActionArgs,
    displayName: 'emitChange',
  });
  const inst_evh_1040167445267876_input_xwdpk3qe8 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_script_s7bhsosw4,
      event: 'input',
      displayName: 'emitChange',
    });
  const inst_evh_1040167445267876_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_input_xwdpk3qe8],
    event: 'input',
  });
  const act_1548630417156826_script_kx7eso0gn = new actionTypes.ScriptAction({
    actionArgs: act_1548630417156826_script_kx7eso0gn_getActionArgs,
    displayName: 'emitChange',
  });
  const inst_evh_1548630417156826_input_awiin1l1n =
    new actionTypes.EventHandlerImpl({
      action: act_1548630417156826_script_kx7eso0gn,
      event: 'input',
      displayName: 'emitChange',
    });
  const inst_evh_1548630417156826_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1548630417156826_input_awiin1l1n],
    event: 'input',
  });

  const actions = {
    inst_evh_8320016629450276_input_vb07wv1i5,
    emitChange: inst_evh_8320016629450276_input_vb07wv1i5,
    inst_evh_8320016629450276_input,
    inst_evh_1040167445267876_input_xwdpk3qe8,
    inst_evh_1040167445267876_input,
    inst_evh_1548630417156826_input_awiin1l1n,
    inst_evh_1548630417156826_input,
  };

  const evh_8320016629450276_input_vb07wv1i5 = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_input_vb07wv1i5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_input = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_input_xwdpk3qe8 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_input_xwdpk3qe8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_input = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1548630417156826_input_awiin1l1n = async (event, scope) => {
    return await actions.inst_evh_1548630417156826_input_awiin1l1n.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1548630417156826_input = async (event, scope) => {
    return await actions.inst_evh_1548630417156826_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <div class="row g-2 align-items-stretch" v-if="loaded">
    <div class="col">
      <div class="input-group">
        <span class="input-group-text">
          <ItpIcon icon="ic:baseline-cable"> </ItpIcon>
        </span>
        <ItpFormSearchableSelect
          name="source"
          v-model="item.source"
          bind-object
          :options="sources"
          valueField="value"
          textField="name"
          clearable
          :readonly="readonly"
          @input="evh_8320016629450276_input($event, {})"
        >
        </ItpFormSearchableSelect>
      </div>
    </div>
    <div class="col">
      <ItpFormNativeSelect
        name="rate_source"
        v-model="item.rate_source"
        :options="rateSources"
        valueField="value"
        textField="name"
        null-option
        :readonly="readonly"
        @input="evh_1040167445267876_input($event, {})"
      >
      </ItpFormNativeSelect>
    </div>
    <div class="col">
      <ItpFormInput
        name="invoice_description"
        v-model="item.invoice_description"
        :readonly="readonly"
        :placeholder="pgettext('ExchangeRateSourceSpec', 'Invoice description')"
        @input="evh_1548630417156826_input($event, {})"
      >
      </ItpFormInput>
    </div>
  </div>
</template>
